import {
  conditionBlockFromZod,
  conditionBlockToZod,
  segmentSchema,
} from "@redotech/redo-customer-segments-common/marketing/segments/segment-zod-schema";
import { ConjunctionMode } from "@redotech/redo-customer-segments-common/segments/segment-types";
import { assertNever } from "@redotech/util/type";
import { ObjectId } from "bson";
import {
  actionStepTypes,
  conditionExpressionSchema,
  entryConditionSchema,
  schemaBooleanExpressionSchema,
  skipConditionSchema,
  type ActionStepType,
  type ConditionExpression,
  type SchemaBooleanExpressionType,
} from "./advanced-flow-db-parser";
import {
  isTypedPublicAutomationStepType,
  type UpdatableAutomationStepType,
} from "./automation-step-catalog";
import { StepOpError } from "./automation-step-ops";
import {
  FORBIDDEN_CHECKOUT_PREDICATE_TYPES,
  formatZodIssues,
  parsePublicTriggerPredicate,
} from "./public-trigger-predicate";
import type { SchemaType } from "./schemas/schemas";
import { ConditionDataSource } from "./steps";
import {
  triggerSpecificFieldsSchema,
  type TriggerRef,
} from "./trigger-specific-fields";

export interface PublicExistingSegmentCondition {
  segmentIds: string[];
}

export interface PublicInlineSegmentCondition {
  conditions: unknown;
}

export interface PublicTriggerDataCondition {
  predicate: unknown;
}

/** GraphQL / public-RPC shape. Exactly one arm. */
export interface PublicConditionExpression {
  existingSegment?: PublicExistingSegmentCondition | null;
  inlineSegment?: PublicInlineSegmentCondition | null;
  triggerData?: PublicTriggerDataCondition | null;
}

export type HydratedPublicConditionExpression =
  | { kind: "existingSegment"; segmentIds: string[] }
  | { kind: "inlineSegment"; conditions: unknown }
  | { kind: "triggerData"; predicate: unknown }
  | { kind: "unsupported"; dataSource: string };

function invalid(message: string): never {
  throw new StepOpError("INVALID_STEP_PATCH", message);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function assertPublicTriggerPredicate(
  value: unknown,
  path = "triggerData.predicate",
): void {
  if (!isRecord(value)) {
    return;
  }
  if (value.type === "checkout_context_predicate") {
    if (path !== "triggerData.predicate") {
      invalid(
        `${path}.type: checkout predicates cannot be nested under an array predicate`,
      );
    }
    if (
      typeof value.predicateType === "string" &&
      FORBIDDEN_CHECKOUT_PREDICATE_TYPES.has(value.predicateType)
    ) {
      invalid(
        `${path}.predicateType: Recover checkout predicates are not supported on public automation conditions`,
      );
    }
  }
  if (value.type === "array_predicate") {
    assertPublicTriggerPredicate(value.condition, `${path}.condition`);
  }
}

function presentArms(expression: PublicConditionExpression): string[] {
  return (
    [
      ["existingSegment", expression.existingSegment],
      ["inlineSegment", expression.inlineSegment],
      ["triggerData", expression.triggerData],
    ] as const
  )
    .filter(([, value]) => value != null)
    .map(([key]) => key);
}

export interface PublicAutomationTrigger extends TriggerRef {
  schemaType: SchemaType;
}

/** Undefined only for a flow with no trigger step, which falls back to the stored grammar. */
export interface PublicConditionContext {
  trigger: PublicAutomationTrigger | undefined;
}

const NO_TRIGGER: PublicConditionContext = { trigger: undefined };

function storedTriggerPredicate(
  predicate: unknown,
  context: PublicConditionContext,
): SchemaBooleanExpressionType {
  assertPublicTriggerPredicate(predicate);
  if (context.trigger == null) {
    const parsed = schemaBooleanExpressionSchema.safeParse(predicate);
    if (!parsed.success) {
      invalid(formatZodIssues("triggerData.predicate", parsed.error));
    }
    return parsed.data;
  }
  const checked = parsePublicTriggerPredicate(
    context.trigger.schemaType,
    predicate,
    "triggerData.predicate",
  );
  if (!checked.success) {
    invalid(checked.message);
  }
  return checked.data;
}

export function publicConditionExpressionToStored(
  expression: PublicConditionExpression,
  context: PublicConditionContext = NO_TRIGGER,
): ConditionExpression {
  const arms = presentArms(expression);
  if (arms.length !== 1) {
    invalid(
      "expression requires exactly one of existingSegment, inlineSegment, or triggerData",
    );
  }
  if (expression.existingSegment != null) {
    const segmentIds = expression.existingSegment.segmentIds.filter(
      (id) => typeof id === "string" && id.length > 0,
    );
    if (segmentIds.length === 0) {
      invalid("existingSegment.segmentIds requires at least one id");
    }
    return { dataSource: ConditionDataSource.ExistingSegment, segmentIds };
  }
  if (expression.inlineSegment != null) {
    const parsed = segmentSchema.safeParse(expression.inlineSegment.conditions);
    if (!parsed.success) {
      invalid(
        "inlineSegment.conditions must match segmentConditionCatalog.conditionsJsonSchema",
      );
    }
    if (parsed.data.conditionBlocks.length !== 1) {
      invalid(
        "inlineSegment.conditions must contain exactly one condition block",
      );
    }
    const block = parsed.data.conditionBlocks[0];
    if (block == null) {
      invalid(
        "inlineSegment.conditions must contain exactly one condition block",
      );
    }
    return {
      dataSource: ConditionDataSource.InlineSegment,
      inlineSegment: conditionBlockFromZod(block),
    };
  }
  const triggerData = expression.triggerData;
  if (triggerData == null) {
    invalid(
      "expression requires exactly one of existingSegment, inlineSegment, or triggerData",
    );
  }
  return {
    dataSource: ConditionDataSource.TriggerData,
    schemaBooleanExpression: storedTriggerPredicate(
      triggerData.predicate,
      context,
    ),
  };
}

export function storedConditionExpressionToPublic(
  expression: unknown,
): HydratedPublicConditionExpression {
  const parsed = conditionExpressionSchema.safeParse(expression);
  if (!parsed.success) {
    const dataSource =
      isRecord(expression) && typeof expression.dataSource === "string"
        ? expression.dataSource
        : "unknown";
    return { kind: "unsupported", dataSource };
  }
  switch (parsed.data.dataSource) {
    case ConditionDataSource.ExistingSegment:
      return {
        kind: "existingSegment",
        segmentIds: parsed.data.segmentIds.filter((id) => id.length > 0),
      };
    case ConditionDataSource.InlineSegment:
      return {
        kind: "inlineSegment",
        conditions: {
          conjunction: ConjunctionMode.AND,
          conditionBlocks: [conditionBlockToZod(parsed.data.inlineSegment)],
        },
      };
    case ConditionDataSource.TriggerData: {
      const predicate = parsed.data.schemaBooleanExpression;
      try {
        assertPublicTriggerPredicate(predicate);
      } catch {
        return {
          kind: "unsupported",
          dataSource: ConditionDataSource.TriggerData,
        };
      }
      return { kind: "triggerData", predicate };
    }
    case ConditionDataSource.OfferResponse:
      return {
        kind: "unsupported",
        dataSource: ConditionDataSource.OfferResponse,
      };
    default:
      return assertNever(parsed.data);
  }
}

/** Action steps the public catalog types. Recover and concierge-block stay out. */
export type PublicSkipActionType = Extract<
  ActionStepType,
  UpdatableAutomationStepType
>;

export const publicSkipActionTypeValues: readonly PublicSkipActionType[] =
  actionStepTypes.filter((type): type is PublicSkipActionType =>
    isTypedPublicAutomationStepType(type),
  );

const PUBLIC_SKIP_ACTION_TYPES = new Set<string>(publicSkipActionTypeValues);

function isPublicSkipActionType(type: string): type is PublicSkipActionType {
  return PUBLIC_SKIP_ACTION_TYPES.has(type);
}

/** Full merchant "all actions" lists read back as omitted. A mix this API cannot store reads as unsupported. */
function publicSkipActionTypes(item: unknown): {
  actionTypes: PublicSkipActionType[] | null;
  unsupported: boolean;
} {
  if (!isRecord(item) || item.actionTypes == null) {
    return { actionTypes: null, unsupported: false };
  }
  if (!Array.isArray(item.actionTypes)) {
    return { actionTypes: null, unsupported: true };
  }
  const stored = item.actionTypes.filter(
    (type): type is string => typeof type === "string",
  );
  if (stored.length === 0) {
    return { actionTypes: null, unsupported: true };
  }
  const publicTypes = stored.filter(isPublicSkipActionType);
  if (publicTypes.length === stored.length) {
    return { actionTypes: publicTypes, unsupported: false };
  }
  const coversEveryAction = actionStepTypes.every((type) =>
    stored.includes(type),
  );
  if (coversEveryAction) {
    return { actionTypes: null, unsupported: false };
  }
  return { actionTypes: null, unsupported: true };
}

export interface PublicSkipCondition {
  expression: HydratedPublicConditionExpression;
  actionTypes: PublicSkipActionType[] | null;
}

export interface PublicConditionGroup<TCondition> {
  conjunction: ConjunctionMode;
  conditions: TCondition[];
}

export interface PublicEntryCondition {
  expression: HydratedPublicConditionExpression;
}

function requireConjunction(value: unknown): ConjunctionMode {
  if (value === ConjunctionMode.AND || value === ConjunctionMode.OR) {
    return value;
  }
  invalid("conjunction must be AND or OR");
}

function requireConditionList(value: unknown, label: string): unknown[] {
  if (!Array.isArray(value)) {
    invalid(`${label}.conditions must be a list`);
  }
  return value;
}

export function publicSkipConditionsToStored(
  group: unknown,
  context: PublicConditionContext = NO_TRIGGER,
): { conjunctionMode: ConjunctionMode; conditions: unknown[] } {
  if (!isRecord(group)) {
    invalid("skipConditions must be an object");
  }
  const conjunctionMode = requireConjunction(group.conjunction);
  const conditions = requireConditionList(
    group.conditions,
    "skipConditions",
  ).map((item) => {
    if (!isRecord(item) || !isRecord(item.expression)) {
      invalid(
        "each skip condition requires exactly one of existingSegment, inlineSegment, or triggerData",
      );
    }
    const stored: Record<string, unknown> = {
      ...publicConditionExpressionToStored(item.expression, context),
      _id: new ObjectId().toString(),
    };
    if (item.actionTypes != null) {
      if (
        !Array.isArray(item.actionTypes) ||
        item.actionTypes.length === 0 ||
        item.actionTypes.some(
          (type) =>
            typeof type !== "string" || !PUBLIC_SKIP_ACTION_TYPES.has(type),
        )
      ) {
        invalid(
          "skip condition actionTypes must be send email, send SMS, manage customer tags, or manage static segment",
        );
      }
      stored.actionTypes = item.actionTypes;
    }
    const parsed = skipConditionSchema.safeParse(stored);
    if (!parsed.success) {
      invalid("skip condition does not match the stored trigger schema");
    }
    return parsed.data;
  });
  return { conjunctionMode, conditions };
}

export function publicEntryConditionsToStored(
  group: unknown,
  context: PublicConditionContext = NO_TRIGGER,
): { conjunctionMode: ConjunctionMode; conditions: unknown[] } {
  if (!isRecord(group)) {
    invalid("entryConditions must be an object");
  }
  const conjunctionMode = requireConjunction(group.conjunction);
  const conditions = requireConditionList(
    group.conditions,
    "entryConditions",
  ).map((item) => {
    if (!isRecord(item) || !isRecord(item.expression)) {
      invalid(
        "each entry condition requires a triggerData expression. Segment membership belongs on skip conditions.",
      );
    }
    const stored = publicConditionExpressionToStored(item.expression, context);
    if (stored.dataSource !== ConditionDataSource.TriggerData) {
      invalid(
        "entry conditions only accept triggerData. Segment membership belongs on skip conditions.",
      );
    }
    const parsed = entryConditionSchema.safeParse({
      _id: new ObjectId().toString(),
      schemaBooleanExpression: stored.schemaBooleanExpression,
    });
    if (!parsed.success) {
      invalid(
        "entry condition predicate is not a valid trigger-schema predicate",
      );
    }
    return parsed.data;
  });
  return { conjunctionMode, conditions };
}

function storedConditionGroup(
  value: unknown,
): { conjunction: ConjunctionMode; conditions: unknown[] } | null {
  if (!isRecord(value) || !Array.isArray(value.conditions)) {
    return null;
  }
  if (
    value.conjunctionMode !== ConjunctionMode.AND &&
    value.conjunctionMode !== ConjunctionMode.OR
  ) {
    return null;
  }
  return { conjunction: value.conjunctionMode, conditions: value.conditions };
}

export function storedSkipConditionsToPublic(
  value: unknown,
): PublicConditionGroup<PublicSkipCondition> | null {
  const group = storedConditionGroup(value);
  if (group == null) {
    return null;
  }
  return {
    conjunction: group.conjunction,
    conditions: group.conditions.map((item) => {
      const actionScope = publicSkipActionTypes(item);
      if (actionScope.unsupported) {
        const dataSource =
          isRecord(item) && typeof item.dataSource === "string"
            ? item.dataSource
            : "unknown";
        return {
          expression: { kind: "unsupported" as const, dataSource },
          actionTypes: null,
        };
      }
      return {
        expression: storedConditionExpressionToPublic(item),
        actionTypes: actionScope.actionTypes,
      };
    }),
  };
}

export function storedEntryConditionsToPublic(
  value: unknown,
): PublicConditionGroup<PublicEntryCondition> | null {
  const group = storedConditionGroup(value);
  if (group == null) {
    return null;
  }
  return {
    conjunction: group.conjunction,
    conditions: group.conditions.map((item) => ({
      expression: storedConditionExpressionToPublic(
        isRecord(item)
          ? {
              dataSource: ConditionDataSource.TriggerData,
              schemaBooleanExpression: item.schemaBooleanExpression,
            }
          : item,
      ),
    })),
  };
}

function storedTriggerSpecificFields(
  value: unknown,
  trigger: TriggerRef,
): unknown {
  const schema = triggerSpecificFieldsSchema(trigger);
  if (schema == null) {
    invalid(
      `triggerSpecificFields: ${trigger.key} has no trigger-specific fields; marketingAutomationCatalog triggerSpecificFieldsJsonSchema is null`,
    );
  }
  const parsed = schema.safeParse(value);
  if (!parsed.success) {
    invalid(formatZodIssues("triggerSpecificFields", parsed.error));
  }
  return parsed.data;
}

export function normalizeTriggerStepPatch(
  patch: Record<string, unknown>,
  context: PublicConditionContext,
): Record<string, unknown> {
  const next = { ...patch };
  if (next.skipConditions != null) {
    next.skipConditions = publicSkipConditionsToStored(
      next.skipConditions,
      context,
    );
  }
  if (next.entryConditions != null) {
    next.entryConditions = publicEntryConditionsToStored(
      next.entryConditions,
      context,
    );
  }
  if (next.triggerSpecificFields != null && context.trigger != null) {
    next.triggerSpecificFields = storedTriggerSpecificFields(
      next.triggerSpecificFields,
      context.trigger,
    );
  }
  return next;
}

export function normalizeConditionStepPatch(
  patch: Record<string, unknown>,
  opts: { requireExpression: boolean } & PublicConditionContext,
): Record<string, unknown> {
  const { expression, ...rest } = patch;
  if (expression == null) {
    if (opts.requireExpression) {
      invalid("condition requires expression");
    }
    return rest;
  }
  if (!isRecord(expression)) {
    invalid(
      "expression requires exactly one of existingSegment, inlineSegment, or triggerData",
    );
  }
  return {
    ...rest,
    expression: publicConditionExpressionToStored(expression, {
      trigger: opts.trigger,
    }),
  };
}
